import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TrendingUp,
  RefreshCw,
  Calendar,
  Package,
  Loader2,
  Clock,
  Star
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTrending = () => {
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('qantri_admin_token');
      const response = await axios.get(`${API}/admin/featured/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching status:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleRotateNow = async () => {
    setRotating(true);
    try {
      const token = localStorage.getItem('qantri_admin_token');
      const response = await axios.post(`${API}/admin/featured/rotate-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la rotazione');
    } finally {
      setRotating(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Mai';
    const date = new Date(isoString);
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-trending">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-serif flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-primary" />
            Prodotti in Evidenza
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestisci i prodotti mostrati in homepage come "In Evidenza"
          </p>
        </div>
        <Button
          onClick={handleRotateNow}
          disabled={rotating}
          className="bg-primary"
          data-testid="rotate-now-btn"
        >
          {rotating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Rotazione...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Ruota Ora
            </>
          )}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Prodotti in Evidenza</p>
                <p className="text-3xl font-bold mt-2">{status?.featured_count || 0}</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center">
                <Star className="h-7 w-7 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Ultima Rotazione</p>
                <p className="text-lg font-bold mt-2">{formatDate(status?.last_rotation)}</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center">
                <Clock className="h-7 w-7 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-700">Prossima Rotazione Automatica</p>
                <p className="text-lg font-bold mt-2 text-green-800">{formatDate(status?.next_rotation)}</p>
                <p className="text-xs text-green-600 mt-1">Ogni {status?.rotation_interval_days || 7} giorni</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-green-200 flex items-center justify-center">
                <Calendar className="h-7 w-7 text-green-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-6">
          <h3 className="font-semibold text-blue-800 mb-2">Come Funziona la Rotazione</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Ogni <strong>7 giorni</strong> il sistema cambia automaticamente i prodotti in evidenza</li>
            <li>• Vengono selezionati <strong>2 prodotti per categoria</strong> in modo casuale</li>
            <li>• I prodotti "brutti" (halloween, skull, etc.) sono esclusi automaticamente</li>
            <li>• Puoi forzare una rotazione manuale cliccando "Ruota Ora"</li>
          </ul>
        </CardContent>
      </Card>

      {/* Featured Products Grid */}
      <div>
        <h2 className="text-xl font-bold font-serif mb-4">Prodotti Attualmente in Evidenza</h2>
        {status?.featured_products?.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nessun prodotto in evidenza</p>
              <Button onClick={handleRotateNow} className="mt-4" disabled={rotating}>
                Avvia Prima Rotazione
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {status?.featured_products?.map((product) => (
              <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-square bg-muted relative">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="h-12 w-12 text-muted-foreground" />
                    </div>
                  )}
                  <span className="absolute top-2 right-2 bg-amber-500 text-white text-xs px-2 py-1 rounded-full">
                    {product.category}
                  </span>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-medium text-sm line-clamp-2 mb-2">{product.title}</h3>
                  <p className="text-lg font-bold text-primary">€{product.price?.toFixed(2)}</p>
                  {product.featured_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      In evidenza dal {new Date(product.featured_at).toLocaleDateString('it-IT')}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTrending;
